// Username validation utilities
export class UsernameValidator {
  private static readonly MIN_LENGTH = 3;
  private static readonly MAX_LENGTH = 30;
  private static readonly ALLOWED_CHARS = /^[a-zA-Z0-9_-]+$/;
  private static readonly RESERVED_WORDS = [
    'admin', 'api', 'app', 'www', 'mail', 'ftp', 'support', 'help',
    'about', 'contact', 'privacy', 'terms', 'legal', 'blog', 'news',
    'wishful', 'getwishful', 'wishlist', 'user', 'users', 'profile',
    'settings', 'account', 'login', 'signup', 'register', 'auth'
  ];

  static validate(username: string): { isValid: boolean; error?: string } {
    // Check length
    if (username.length < this.MIN_LENGTH) {
      return { isValid: false, error: `Username must be at least ${this.MIN_LENGTH} characters long` };
    }
    
    if (username.length > this.MAX_LENGTH) {
      return { isValid: false, error: `Username must be no more than ${this.MAX_LENGTH} characters long` };
    }

    // Check allowed characters
    if (!this.ALLOWED_CHARS.test(username)) {
      return { isValid: false, error: 'Username can only contain letters, numbers, underscores, and hyphens' };
    }

    // Check reserved words
    if (this.RESERVED_WORDS.includes(username.toLowerCase())) {
      return { isValid: false, error: 'This username is reserved and cannot be used' };
    }

    // Check if starts/ends with special characters
    if (username.startsWith('-') || username.startsWith('_') || 
        username.endsWith('-') || username.endsWith('_')) {
      return { isValid: false, error: 'Username cannot start or end with underscores or hyphens' };
    }

    return { isValid: true };
  }

  static async checkAvailability(username: string, supabase: any, currentUserId?: string): Promise<{ available: boolean; error?: string }> {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('username, id')
        .eq('username', username.toLowerCase())
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (data) {
        // If the username belongs to the current user, it's available for them to keep
        if (currentUserId && data.id === currentUserId) {
          return { available: true };
        }
        return { available: false, error: 'This username is already taken' };
      }

      return { available: true };
    } catch (error) {
      console.error('Error checking username availability:', error);
      return { available: false, error: 'Unable to check username availability' };
    }
  }

  static generateSuggestions(baseUsername: string): string[] {
    const suggestions: string[] = [];
    const cleanBase = baseUsername.toLowerCase().replace(/[^a-z0-9]/g, '');
    
    // Add numbers
    for (let i = 1; i <= 5; i++) {
      suggestions.push(`${cleanBase}${i}`);
    }
    
    // Add common suffixes
    const suffixes = ['_', '-', 'user', 'official'];
    suffixes.forEach(suffix => {
      suggestions.push(`${cleanBase}${suffix}`);
    });
    
    // Add random characters
    const randomChars = ['x', 'z', 'pro', 'app'];
    randomChars.forEach(char => {
      suggestions.push(`${cleanBase}${char}`);
    });

    return suggestions.slice(0, 8); // Return top 8 suggestions
  }
}
